/**
 * Mastering targets and character — the shared reference data for the Master panel.
 *
 * WHAT IS REAL HERE: the loudness targets. −14 LUFS for streaming and YouTube, −16 for
 * quieter social playback — published normalisation figures the platforms actually apply,
 * and the reason a master is made to a target at all.
 *
 * The backend now measures and renders real masters when available, but the client-side
 * responsibility is still this file's: stable target tables, bounded custom-target maths,
 * and human-readable labels that keep the UI and request payloads in agreement.
 *
 * `gainToTarget` therefore takes a MEASURED loudness and works out the gap. The arithmetic
 * is simple; the important part is that the measurement must be supplied rather than guessed.
 */

export interface MasterTarget {
  id: string;
  label: string;
  /** Integrated loudness the platform normalises to, in LUFS. */
  lufs: number;
  /** True peak ceiling in dBTP, which is the other half of a delivery spec. */
  truePeak: number;
  description: string;
}

/**
 * The delivery targets.
 *
 * Figures from the platforms' own published specs: Spotify and Apple Music normalise to
 * −14 LUFS, YouTube to −14, and −16 is the conventional target for social video where
 * playback starts quiet and often muted. "General" is −14 as well but named separately
 * because choosing it is a different decision — no specific platform in mind.
 */
export const MASTER_TARGETS: MasterTarget[] = [
  {
    id: 'streaming',
    label: 'Streaming',
    lufs: -14,
    truePeak: -1,
    description: 'Spotify, Apple Music and most streaming services.',
  },
  {
    id: 'youtube',
    label: 'YouTube',
    lufs: -14,
    truePeak: -1,
    description: 'YouTube normalises to the same figure as streaming.',
  },
  {
    id: 'social',
    label: 'Social',
    lufs: -16,
    truePeak: -1.5,
    description: 'Quieter, for feeds that autoplay low or muted.',
  },
  {
    id: 'general',
    label: 'General',
    lufs: -14,
    truePeak: -1,
    description: 'No particular platform. A safe all-round level.',
  },
  {
    id: 'custom',
    label: 'Custom',
    lufs: -14,
    truePeak: -1,
    description: 'Set the loudness target yourself.',
  },
];

export interface MasterCharacter {
  id: string;
  label: string;
  /** What the mastering profile emphasises, in user-facing terms rather than DSP numbers. */
  description: string;
}

/**
 * The five characters, which are EQ-and-compression intents rather than settings.
 *
 * No numbers attached, and that is deliberate: "Warm" is a curve a mastering engineer or a
 * trained model chooses in context, not a fixed +2 dB at 200 Hz. Publishing invented
 * figures would make this look like a spec someone could check.
 */
export const MASTER_CHARACTERS: MasterCharacter[] = [
  { id: 'balanced', label: 'Balanced', description: 'Even-handed. Changes the least.' },
  { id: 'loud', label: 'Loud', description: 'Denser and more limited, for impact.' },
  { id: 'dynamic', label: 'Dynamic', description: 'Keeps the quiet parts quiet.' },
  { id: 'warm', label: 'Warm', description: 'Fuller low-mids, softer top end.' },
  { id: 'bright', label: 'Bright', description: 'More air and detail up high.' },
];

/** Looks a target up by id, falling back to Streaming so a stale id cannot blank the UI. */
export function masterTarget(id: string): MasterTarget {
  return MASTER_TARGETS.find((t) => t.id === id) ?? MASTER_TARGETS[0];
}

/** Looks a character up by id, falling back to Balanced. */
export function masterCharacter(id: string): MasterCharacter {
  return MASTER_CHARACTERS.find((ch) => ch.id === id) ?? MASTER_CHARACTERS[0];
}

/** The custom target's bounds. Beyond these a "master" is a different operation. */
export const MIN_LUFS = -24;
export const MAX_LUFS = -6;

/** Clamps a custom loudness target into the supported range. */
export function clampLufs(lufs: number): number {
  return Math.max(MIN_LUFS, Math.min(MAX_LUFS, Math.round(lufs)));
}

/**
 * How much gain change reaches the target from a MEASURED loudness.
 *
 * Positive means the track must come up, negative means down. Takes the measurement as an
 * argument and has no default, so it cannot be called without one — a fabricated "assume
 * −9 LUFS" would produce a confident number about a track nobody has listened to.
 */
export function gainToTarget(measuredLufs: number, targetLufs: number): number {
  return Math.round((targetLufs - measuredLufs) * 10) / 10;
}

/** A LUFS figure as the UI writes it — "−14 LUFS". */
export function formatLufs(lufs: number): string {
  // U+2212 minus, not a hyphen: it is the character the rest of the app's numbers use and
  // it is read correctly as "minus" rather than as a dash.
  return `${lufs < 0 ? '−' : ''}${Math.abs(lufs)} LUFS`;
}
